import { HttpEventType } from '@angular/common/http';
import { Component } from '@angular/core';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { BulkImportResult } from 'src/app/components/bulk-import/bulk-import-result';
import { BulkImportService } from 'src/app/services/bulk-import.service';
import { FileService } from 'src/app/services/file.service';
import { ToastService } from 'src/app/services/toast-service.service';

export type FileUploadState = 'pending' | 'uploading' | 'done' | 'error';

export interface FileUploadRow {
  name: string;
  state: FileUploadState;
  progress: number;
  message?: string;
}

@Component({
    selector: 'app-bulk-import',
    templateUrl: './bulk-import.component.html',
    styleUrls: ['./bulk-import.component.scss'],
    standalone: false
})
export class BulkImportComponent {
  pageName = CheckPageAccess.BULKIMPORT;

  // Step 1: bulk-upload the e-book PDFs and course thumbnails the workbook will reference by filename.
  pdfFiles: File[] = [];
  pdfRows: FileUploadRow[] = [];
  isUploadingPdfs = false;

  thumbnailFiles: File[] = [];
  thumbnailRows: FileUploadRow[] = [];
  isUploadingThumbnails = false;

  // Step 2: the metadata workbook itself.
  selectedFile: File | null = null;
  isDownloading = false;
  isImporting = false;
  result: BulkImportResult | null = null;

  constructor(
    private bulkImportService: BulkImportService,
    private fileService: FileService,
    private toastservice: ToastService
  ) { }

  onPdfFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.pdfFiles = input.files ? Array.from(input.files) : [];
    this.pdfRows = this.pdfFiles.map(f => ({ name: f.name, state: 'pending' as FileUploadState, progress: 0 }));
  }

  onThumbnailFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.thumbnailFiles = input.files ? Array.from(input.files) : [];
    this.thumbnailRows = this.thumbnailFiles.map(f => ({ name: f.name, state: 'pending' as FileUploadState, progress: 0 }));
  }

  async uploadAllPdfs() {
    if (this.isUploadingPdfs || this.pdfFiles.length === 0) {
      return;
    }

    this.isUploadingPdfs = true;
    for (let i = 0; i < this.pdfFiles.length; i++) {
      const file = this.pdfFiles[i];
      const row = this.pdfRows[i];
      row.state = 'uploading';
      row.progress = 0;
      try {
        await this.uploadMaterialFileAndWait(file, row);
        row.state = 'done';
        row.progress = 100;
      } catch {
        row.state = 'error';
        row.message = 'Upload failed';
      }
    }
    this.isUploadingPdfs = false;

    const failed = this.pdfRows.filter(r => r.state === 'error').length;
    if (failed > 0) {
      this.toastservice.showErrorMessage(`${failed} of ${this.pdfRows.length} PDF upload(s) failed. Retry the failed ones.`);
    } else {
      this.toastservice.showMessage(`Uploaded ${this.pdfRows.length} PDF(s) successfully.`);
    }
  }

  async uploadAllThumbnails() {
    if (this.isUploadingThumbnails || this.thumbnailFiles.length === 0) {
      return;
    }

    this.isUploadingThumbnails = true;
    for (let i = 0; i < this.thumbnailFiles.length; i++) {
      const file = this.thumbnailFiles[i];
      const row = this.thumbnailRows[i];
      row.state = 'uploading';
      row.progress = 0;
      try {
        await this.uploadGenericFileAndWait(file, row);
        row.state = 'done';
        row.progress = 100;
      } catch {
        row.state = 'error';
        row.message = 'Upload failed';
      }
    }
    this.isUploadingThumbnails = false;

    const failed = this.thumbnailRows.filter(r => r.state === 'error').length;
    if (failed > 0) {
      this.toastservice.showErrorMessage(`${failed} of ${this.thumbnailRows.length} thumbnail upload(s) failed. Retry the failed ones.`);
    } else {
      this.toastservice.showMessage(`Uploaded ${this.thumbnailRows.length} thumbnail(s) successfully.`);
    }
  }

  private uploadMaterialFileAndWait(file: File, row: FileUploadRow): Promise<void> {
    return new Promise((resolve, reject) => {
      this.fileService.uploadMaterialFileDirect(file).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            row.progress = Math.min(99, Math.round((100 * event.loaded) / event.total));
          } else if (event.type === HttpEventType.Response) {
            resolve();
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  private uploadGenericFileAndWait(file: File, row: FileUploadRow): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      this.fileService.uploadfile(formData).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            row.progress = Math.min(99, Math.round((100 * event.loaded) / event.total));
          } else if (event.type === HttpEventType.Response) {
            resolve();
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  downloadTemplate() {
    this.isDownloading = true;
    this.bulkImportService.downloadTemplate().subscribe({
      next: (response) => {
        this.isDownloading = false;
        const fileUrl = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = 'CourseMaterialBulkImportTemplate.xlsx';
        link.click();
        URL.revokeObjectURL(fileUrl);
      },
      error: () => {
        this.isDownloading = false;
        this.toastservice.showErrorMessage('Failed to download the template. Please try again.');
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files && input.files.length > 0 ? input.files[0] : null;
    this.result = null;
  }

  upload() {
    if (!this.selectedFile || this.isImporting) {
      return;
    }

    const unfinished = [...this.pdfRows, ...this.thumbnailRows].filter(r => r.state !== 'done').length;
    if (unfinished > 0) {
      this.toastservice.showErrorMessage(`${unfinished} file(s) in Step 1 haven't finished uploading (still pending/uploading/error). The import will fail for any row that references them. Upload those first, or proceed only if you're sure those files were already uploaded in an earlier session.`);
    }

    this.isImporting = true;
    this.result = null;
    this.bulkImportService.importCoursesAndMaterials(this.selectedFile).subscribe({
      next: (response) => {
        this.isImporting = false;
        this.result = response;
        if (response.success) {
          this.toastservice.showMessage(`Import complete: ${response.coursesCreated} course(s), ${response.materialsCreated} material(s) created.`);
          this.selectedFile = null;
        } else {
          this.toastservice.showErrorMessage('The workbook had errors and nothing was imported. See the details below.');
        }
      },
      error: () => {
        this.isImporting = false;
        this.toastservice.showErrorMessage('Failed to import the workbook. Please try again.');
      }
    });
  }
}
